<template>
    <section class="py-16 bg-backgroundLight">
        <div class="w-full mx-auto px-1 sm:px-3 md:px-6 xl:max-w-7xl">
            <h2 class="text-3xl font-bold text-center text-primary mb-8">
                Classement
                <span v-if="currentLevel === 'global'"> Général</span>
                <span v-else-if="currentLevel === 'region'"> - {{ currentRegionName }}</span>
                <span v-else-if="currentLevel === 'department'"> - {{ currentDepartmentName }}</span>
                <span v-else-if="currentLevel === 'commune'"> - {{ currentCityName }}</span>
            </h2>

            <div class="grid md:grid-cols-3 gap-4 md:gap-8" style="height: fit-content;">
                <div
                    ref="mapMountRef"
                    class="md:col-span-2 bg-white rounded-lg shadow-lg p-2 sm:p-3 md:p-4 flex items-center justify-center">
                    <div class="text-secondary italic w-full h-full">
                        <AsyncCarteFrance v-if="shouldRenderMap" :top-users="ranking" :hovered-username="hoveredUsername"
                            @region-selected="onRegionSelected" @department-selected="onDepartmentSelected"
                            @commune-hover="onCommuneHover" @commune-hover-out="onCommuneHoverOut"
                            @user-hover="onUserHover" />
                        <div v-else class="w-full min-h-[320px] sm:min-h-[420px] grid place-items-center text-sm text-secondary">
                            Chargement de la carte...
                        </div>
                    </div>
                </div>

                <!-- Top 10 Classement -->
                <div class="w-full min-w-0 mt-10 sm:mt-10 md:mt-0 bg-white rounded-lg shadow-lg p-3 sm:p-4 md:p-6">
                    <TiltedHeader>Top 10</TiltedHeader>

                    <ul v-if="ranking.length" class="w-full space-y-2 m-0 p-0 list-none">
                        <li v-for="(user, index) in ranking" :key="user.username" :class="[
                            'w-full box-border flex justify-between items-center border-b border-secondary/30 pb-2 rounded-lg !p-2 transition-all cursor-pointer',
                            getCardClass(index),
                            hoveredUsername === user.username ? 'highlighted-user' : ''
                        ]" @mouseover="onUserHover(user.username)" @mouseout="onUserHover(null)">
                            <div class="flex-1 min-w-0">
                                <span class="block truncate">{{ index + 1 }}. {{ user.username }}</span>
                            </div>
                            <div class="ml-3 flex-shrink-0 whitespace-nowrap">
                                <span :class="index == 1 ? 'text-primary font-bold' : 'text-gold font-bold'">
                                    {{ user.amount }}€ ({{ user.amount }} pts)
                                </span>
                            </div>
                        </li>
                    </ul>

                    <p v-else class="text-center text-secondary italic mt-4">
                        Aucun classement à afficher.
                    </p>
                </div>
            </div>
        </div>
    </section>
</template>
<script setup>
import { ref, onMounted, onBeforeUnmount, defineAsyncComponent } from 'vue';
import TiltedHeader from '../titles/TiltedHeader.vue';
import RankingService from '@/services/ranking.service';

const AsyncCarteFrance = defineAsyncComponent(() => import('../map/CarteFrance.vue'));

const ranking = ref([]);

const currentLevel = ref('global');

const currentRegionId = ref(null);
const currentRegionName = ref(null);

const currentDepartmentId = ref(null);
const currentDepartmentName = ref(null);

const currentCityId = ref(null);
const currentCityName = ref(null);

const hoveredUsername = ref(null);
const mapMountRef = ref(null);
const shouldRenderMap = ref(false);
let mapObserver = null;

const loadRanking = async (filters = {}) => {
    try {
        const response = await RankingService.getTopUsers(filters);
        ranking.value = response;
    } catch (error) {
        console.error('Erreur lors du chargement du classement :', error);
        ranking.value = [];
    }
};

const onRegionSelected = (region) => {
    currentLevel.value = region ? 'region' : 'global';

    currentRegionId.value = region?.id || null;
    currentRegionName.value = region?.name || null;

    currentDepartmentId.value = null;
    currentDepartmentName.value = null;
    currentCityId.value = null;
    currentCityName.value = null;
    loadRanking(region ? { regionId: region.id } : {});
};

const onDepartmentSelected = (department) => {
    currentLevel.value = 'department'

    currentDepartmentId.value = department.id
    currentDepartmentName.value = department.name

    currentCityId.value = null
    currentCityName.value = null
    activeCommuneId = null

    loadRanking({ departmentId: department.id })
}

let communeHoverTimer = null
let activeCommuneId = null


const onCommuneHover = (city) => {
    if (!city || city.id === activeCommuneId) return

    clearTimeout(communeHoverTimer)

    communeHoverTimer = setTimeout(() => {
        activeCommuneId = city.id
        currentLevel.value = 'commune'
        currentCityId.value = city.id
        currentCityName.value = city.name

        loadRanking({ cityId: city.id })
    }, 300) // ← délai UX (200–400ms idéal)
}

const onCommuneHoverOut = () => {
    clearTimeout(communeHoverTimer)

    if (currentLevel.value !== 'commune') return

    activeCommuneId = null
    currentLevel.value = 'department'
    currentCityId.value = null
    currentCityName.value = null

    loadRanking({ departmentId: currentDepartmentId.value })
}

const onUserHover = (username) => {
    hoveredUsername.value = username;
}



// ✅ Fonction utilitaire pour donner la classe selon le rang
const getCardClass = (index) => {
    switch (index) {
        case 0:
            return 'card-gold-premium text-gold font-semibold';
        case 1:
            return 'card-gold text-primary font-semibold';
        case 2:
            return 'card-secondary text-white font-semibold';
        default:
            return 'bg-white hover:bg-gray-50';
    }
};

onMounted(() => {
    loadRanking();

    if (!('IntersectionObserver' in window)) {
        shouldRenderMap.value = true;
        return;
    }

    mapObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        shouldRenderMap.value = true;
        mapObserver?.disconnect();
        mapObserver = null;
    }, { rootMargin: '200px 0px' });

    if (mapMountRef.value) {
        mapObserver.observe(mapMountRef.value);
    }
});

onBeforeUnmount(() => {
    clearTimeout(communeHoverTimer);
    mapObserver?.disconnect();
});
</script>

<style scoped>
/* Mise en évidence de l'utilisateur survolé sur la carte */
.highlighted-user {
    transform: scale(1.02);
    box-shadow: 0 0 15px rgba(212, 175, 55, 0.6) !important;
    animation: pulse-highlight 1.5s ease-in-out infinite;
}

@keyframes pulse-highlight {

    0%,
    100% {
        box-shadow: 0 0 15px rgba(212, 175, 55, 0.6);
    }

    50% {
        box-shadow: 0 0 25px rgba(212, 175, 55, 0.9);
    }
}
</style>
